import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { PageHeader } from "@/components/shared/PageHeader";
import { DataTable } from "@/components/shared/DataTable";
import { EmptyState } from "@/components/shared/EmptyState";
import { FormModal } from "@/components/shared/FormModal";
import { StatusPill } from "@/components/shared/StatusPill";
import { Button } from "@/components/ui/button";
import { Flag, Plus, Trash2, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function Initiatives() {
  const { has } = useAtlas();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(false);
  const canEdit = has("tactics.projects.edit");
  const load = () => api.get("/tactics/initiatives").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/work/boards").then((r) => { const c = {}; (r.data.boards || []).forEach((b) => { if (b.initiative_id) c[b.initiative_id] = (c[b.initiative_id] || 0) + 1; }); setCounts(c); }).catch(() => {});
  }, []);
  const create = async (v) => { await api.post("/tactics/initiatives", v); toast.success("Mission created"); load(); };
  const remove = async (r) => {
    if (!window.confirm(`Delete mission "${r.name}"? Its boards are kept and just un-linked.`)) return;
    try { await api.delete(`/tactics/initiatives/${r.id}`); toast.success("Mission deleted"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div>
      <PageHeader title="Missions" subtitle="Named containers that group project boards, click one to see its boards">
        {canEdit && <Button onClick={() => setOpen(true)} data-testid="initiative-create-button"><Plus className="w-4 h-4 mr-1" /> New Mission</Button>}
      </PageHeader>
      {items.length === 0 ? (
        <EmptyState icon={Flag} title="No missions yet" description="Create a mission to group related project boards." actionLabel={canEdit ? "New Mission" : undefined} onAction={() => setOpen(true)} />
      ) : (
        <DataTable testid="tactics-initiatives-table" rowTestId="initiative-row" onRowClick={(r) => navigate(`/work/boards?initiative=${r.id}`)} columns={[
          { key: "name", header: "Mission", render: (r) => <span className="font-medium text-slate-900">{r.name}</span> },
          { key: "status", header: "Status", render: (r) => <StatusPill value={r.status} /> },
          { key: "boards", header: "Boards", render: (r) => <span className="text-slate-600 tabular">{counts[r.id] || 0}</span> },
          { key: "description", header: "Description", render: (r) => <span className="text-slate-500">{r.description || "—"}</span> },
          { key: "actions", header: "", align: "right", render: (r) => <span className="flex items-center justify-end gap-2">{canEdit && <button onClick={(e) => { e.stopPropagation(); remove(r); }} className="text-slate-400 hover:text-red-600" data-testid={`initiative-delete-${r.id}`}><Trash2 className="w-4 h-4" /></button>}<ChevronRight className="w-4 h-4 text-slate-300" /></span> },
        ]} rows={items} />
      )}
      <FormModal open={open} onOpenChange={setOpen} title="New Mission" testid="initiative-form-modal" onSubmit={create}
        fields={[
          { name: "name", label: "Name", required: true },
          { name: "description", label: "Description", type: "textarea" },
          { name: "status", label: "Status", type: "select", default: "active", options: ["active", "paused", "done"].map((v) => ({ value: v, label: v })) },
        ]} />
    </div>
  );
}
